import { useMemo, useState } from 'react'

import { publishEvent } from '@zet/analytics'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import {
  BTN,
  BottomSheet,
  H4,
  Icon,
  Pressable,
  SH3,
  Stack,
  colors,
} from '@zet/design-system'
import { useExperiments } from '@zet/experiments'
import { useTicketID, useTicketList } from '@zet/help-support/src/api/hooks'
import { SupportHeadphones } from '@zet/help-support/src/assets/svg/'
import { PressableCtaWithIcon } from '@zet/help-support/src/component/PressableCtaWithIcon'
import { Cta } from '@zet/help-support/src/component/cta'
import { useChatStore } from '@zet/help-support/store/chatStore'
import { i18n } from '@zet/localization'

import WaitIcon from '../../assets/svg/waitIcon.svg'

const HelpCTA = ({ leadId, enabled }: { leadId: number; enabled: boolean }) => {
  const { data: experiments } = useExperiments()
  const setConversation = useChatStore((state) => state.setConversation)
  const { navigate } = useNavigation()
  const { data: ticketData } = useTicketList({ status: 'IN_PROGRESS', leadId })
  const { data: incompleteTicketData } = useTicketList({
    status: 'INCOMPLETE',
    leadId,
  })
  const { data: closedTicketData } = useTicketList({
    status: 'CLOSED',
    leadId,
  })
  const { ticketsCount } = useMemo(() => {
    const ticketsCount = ticketData?.pages.flat(Infinity)?.[0]?.count
    return { ticketsCount }
  }, [ticketData])

  const { incompleteTicketsCount, incompleteTicketId } = useMemo(() => {
    const incompleteTicketsCount =
      incompleteTicketData?.pages.flat(Infinity)?.[0]?.count
    const incompleteTicketId =
      incompleteTicketData?.pages.flat(Infinity)?.[0]?.tickets[0]?.id
    return { incompleteTicketsCount, incompleteTicketId }
  }, [incompleteTicketData])

  const { closedTicketsCount } = useMemo(() => {
    const closedTicketsCount =
      closedTicketData?.pages.flat(Infinity)?.[0]?.count
    return { closedTicketsCount }
  }, [closedTicketData])

  const { refetch: createTicketId } = useTicketID(leadId)
  const [showComplaintWindowModal, setShowComplaintWindowModal] =
    useState(false)

  const handleTicketListPress = () => {
    navigate('HelpAndSupport', { leadId })
  }

  const handleResumeTicketPress = () => {
    navigate('ChatHistory', { ticketID: incompleteTicketId })
  }

  const createNewTicketPress = () => {
    publishEvent('click_action', {
      item_name: enabled ? 'raise_complaint' : 'raise_complaint_understood',
    })

    if (!enabled) {
      setShowComplaintWindowModal(true)
      return
    }

    setConversation({ type: 'clearChat' })
    createTicketId().then(({ data }) => {
      data?.id && navigate('HelpSupport', { ticketID: data?.id })
    })
  }

  const handleViewAllPress = () => {
    publishEvent('click_event', { item: 'lead_page_view_all_past_tickets' })
    navigate('TicketsList')
  }

  if (experiments?.UI_isRaiseComplaint) {
    return (
      <>
        {ticketsCount ? (
          <PressableCtaWithIcon
            leftIcon={<SupportHeadphones />}
            heading={i18n.t('myNetwork.helpSupportTicketRaised.heading')}
            subHeading={i18n.t('myNetwork.helpSupportTicketRaised.subHeading', {
              totalTickets: ticketsCount,
            })}
            onPress={handleTicketListPress}
            eventName="click_action"
            eventPayload={{ item: 'lead_page_ticket_raised' }}
          />
        ) : incompleteTicketsCount !== 0 ? (
          <PressableCtaWithIcon
            eventName="click_action"
            eventPayload={{
              item_name: 'profile_help_support',
              title: 'Resume your complaints',
            }}
            onPress={handleResumeTicketPress}
            headingColor={colors.warning[150]}
            heading={i18n.t('myNetwork.helpSupportTicketIncomplete.heading')}
            subHeading={i18n.t(
              'myNetwork.helpSupportTicketIncomplete.subHeading',
              {
                complaints: incompleteTicketsCount,
              }
            )}
            leftIcon={
              <Icon type="CautionOutlined" color={colors.warning[150]} />
            }
          />
        ) : (
          <>
            <LeadComplaintWindowBottomSheet
              visible={showComplaintWindowModal}
              onDismiss={() => setShowComplaintWindowModal(false)}
            />
            <PressableCtaWithIcon
              heading={i18n.t(
                'myNetwork.helpSupportDataRaiseComplaint.heading'
              )}
              subHeading={i18n.t(
                enabled
                  ? 'myNetwork.helpSupportDataRaiseComplaint.subHeading'
                  : 'myNetwork.helpSupportDataRaiseComplaint.subHeadingDisabled'
              )}
              onPress={createNewTicketPress}
              eventName="click_action"
              eventPayload={{ item: 'lead_page_raise_complaint' }}
            />
            {closedTicketsCount !== 0 && (
              <Cta onPress={handleViewAllPress} cta={'View all Past Tickets'} />
            )}
          </>
        )}
      </>
    )
  }

  return <></>
}

export default HelpCTA

const LeadComplaintWindowBottomSheet = ({
  visible,
  onDismiss,
}: {
  visible: boolean
  onDismiss: () => void
}) => {
  return (
    <BottomSheet
      visible={visible}
      onDismiss={onDismiss}
      sheetName="raise_complaint"
    >
      <Stack className="p-4">
        <Stack className="items-center pb-3">
          <WaitIcon />
        </Stack>
        <H4 className="px-3 text-center">
          {i18n.t(
            'myNetwork.helpSupportDataRaiseComplaint.complaintWindowModalHeading'
          )}
        </H4>
        <SH3 className="text-grays-70 px-2 pt-3 text-center">
          {i18n.t(
            'myNetwork.helpSupportDataRaiseComplaint.complaintWindowModalSubHeading'
          )}
        </SH3>
        <Pressable
          className="border-primary-100 mt-4 rounded-md border bg-white py-2 text-center"
          onPress={onDismiss}
        >
          <BTN className="text-primary-100 text-center">
            {i18n.t(
              'myNetwork.helpSupportDataRaiseComplaint.complaintWindowModalCTA'
            )}
          </BTN>
        </Pressable>
      </Stack>
    </BottomSheet>
  )
}
